import {
  ChartConfig,
  ChartSeries,
  ChartSeriesOptions,
  ChartVisibleRateTypes,
  ChartSampleSpan,
  MarketHistorySeries,
} from 'models/chart'
import { formatNumber } from 'utils/format-number'
import dayjs from 'dayjs'
import 'dayjs/locale/pl'
import { getTranslationsMap } from 'i18n/i18n'
import { MarketBrief, MarketBriefBestOffers } from 'models/market'
import { storageService } from 'services/storage'

const LOCALSTORAGE_INDEX = 'wtCurrencyPairChart'
const LOCALSTORAGE_VALIDITY = 604800 // 7 days

const chartDefaultSeriesOptions: Pick<ChartSeriesOptions, 'animation' | 'type' | 'showInNavigator' | 'dataGrouping'> = {
  animation: true,
  type: 'line',
  showInNavigator: true,
  dataGrouping: {
    enabled: false,
  },
}

const currencyChartHelper = {
  prepareSeriesForChart: (series: MarketHistorySeries, visibleRateTypes: ChartVisibleRateTypes): ChartSeries => ({
    bid: {
      ...chartDefaultSeriesOptions,
      name: getTranslationsMap().highcharts.rateTypes.bid,
      colorIndex: 0,
      visible: visibleRateTypes.bid,
      data: series.wtBidSeries.map(([ts, bidClose]) => [ts * 1000, bidClose]),
    },
    ask: {
      ...chartDefaultSeriesOptions,
      name: getTranslationsMap().highcharts.rateTypes.ask,
      colorIndex: 1,
      visible: visibleRateTypes.ask,
      data: series.wtAskSeries.map(([ts, askClose]) => [ts * 1000, askClose]),
    },
    forex: {
      ...chartDefaultSeriesOptions,
      name: getTranslationsMap().highcharts.rateTypes.forex,
      colorIndex: 2,
      visible: visibleRateTypes.forex,
      data: series.fxSeries.map(([ts, fx]) => [ts * 1000, fx]),
    },
  }),

  updateSeriesWithMarketBrief: (
    fxSeries: number[][],
    wtSeries: number[][],
    marketBriefBestOffers: MarketBriefBestOffers | null,
  ): MarketHistorySeries => {
    if (!marketBriefBestOffers) {
      return {
        fxSeries: fxSeries.map(([ts, fxClose]) => [ts, fxClose]),
        ...currencyChartHelper.divideWtSeries(wtSeries),
      }
    }

    const { bid_now: bid, ask_now: ask, forex_now: forex } = marketBriefBestOffers
    const tsNowMiliseconds = Math.trunc(Date.now() / 1000)
    const tsNowSeconds = tsNowMiliseconds - (tsNowMiliseconds % 60)
    return {
      fxSeries: fxSeries.map(([ts, fx], i) => (i === fxSeries.length - 1 ? [tsNowSeconds, forex] : [ts, fx])),
      wtBidSeries: wtSeries.map(([ts, bidClose], i) =>
        i === wtSeries.length - 1 ? [tsNowSeconds, bid] : [ts, bidClose],
      ),
      wtAskSeries: wtSeries.map(([ts, , , , askClose], i) =>
        i === wtSeries.length - 1 ? [tsNowSeconds, ask] : [ts, askClose],
      ),
    }
  },

  getCurrentMarketBrief: (marketBrief: MarketBrief | null): MarketBriefBestOffers => {
    if (!marketBrief) {
      return null
    }
    const currentMarketBrief = marketBrief
    return currentMarketBrief ? currentMarketBrief.bestOffers : null
  },

  divideWtSeries: (wtSeries: number[][]): Pick<MarketHistorySeries, 'wtAskSeries' | 'wtBidSeries'> => ({
    wtBidSeries: wtSeries.map(([ts, bidClose]) => [ts, bidClose]),
    wtAskSeries: wtSeries.map(([ts, , , , askClose]) => [ts, askClose]),
  }),

  getDefaultChartOptions: (sampleSpan: ChartSampleSpan, ratePrecision: number) => ({
    chart: {
      animation: true,
      marginLeft: 48,
    },
    rangeSelector: {
      enabled: false,
    },
    legend: {
      enabled: false,
    },
    navigator: {
      enabled: false,
    },
    scrollbar: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    plotOptions: {
      series: {
        showInNavigator: false,
      },
    },
    tooltip: {
      headerFormat: '<span style="font-size: 10px">{point.key}</span><br/>',
      xDateFormat: '%A, %b %e, %Y, %H:%M',
      pointFormat:
        '<span style="display:block; text-align:right;" class="series-{series.colorIndex}">' +
        '{series.name}</span>: <b>{point.y}</b><br/>',
      valueDecimals: ratePrecision,
      backgroundColor: '#fff',
      borderColor: '#D4DAE0',
      borderRadius: 5,
    },
    xAxis: {
      tickLength: 0,
      labels: {
        formatter() {
          const daysAgo = (Date.now() - this.value) / 3600000 / 24
          const hhmm = sampleSpan.indexOf('1m') > -1 || sampleSpan.indexOf('3m') > -1
          const ddmm = !hhmm || daysAgo > 1
          const year = daysAgo > 360
          return dayjs(this.value)
            .locale('pl')
            .format((hhmm ? 'HH:mm ' : '') + (ddmm ? 'DD. MMM. ' : '') + (year ? 'YYYY' : ''))
        },
      },
      lineColor: '#D4DAE0',
      gridLineWidth: 1,
      showFirstLabel: true,
    },
    yAxis: {
      labels: {
        formatter() {
          return formatNumber(this.value, ratePrecision)
        },
        y: 5,
        x: -3,
        padding: 2,
        align: 'right',
      },
      tickLength: 0,
      opposite: false,
      showFirstLabel: true,
      showLastLabel: true,
      minPadding: 0.02,
      maxPadding: 0,
    },
  }),

  getLocalStorageConfig: (): ChartConfig => {
    const dataFromLocalstorage = storageService.getItem(LOCALSTORAGE_INDEX)

    try {
      const { stored, ...parsedDataFromLocalstorage } = dataFromLocalstorage ? JSON.parse(dataFromLocalstorage) : null
      return parsedDataFromLocalstorage && stored + LOCALSTORAGE_VALIDITY >= Date.now() / 1000
        ? parsedDataFromLocalstorage
        : null
    } catch (e) {
      return null
    }
  },

  storeLocalStorageConfig: (config: ChartConfig): void => {
    const toStore = JSON.stringify({ ...config, stored: (Date.now() / 1000) | 0 })
    storageService.setItem(LOCALSTORAGE_INDEX, toStore)
  },
}

export default currencyChartHelper
